import {
  readCustomer,
  readMessageTemplate,
  readPayment,
  readCardPayout,
  PermissionsGuard,
  readLedgerPayment,
  AuthGuard,
  LogoutPage,
  TokenGuard,
  UserGuard,
  UserProvider,
  useUser,
  MerchantProvider,
  MerchantGuard,
  useMerchant,
} from "@auth";
import {
  Transactions,
  Requests,
  Accounts,
  Templates,
  Payouts,
  Settings,
  Collections,
  AuthoriseRefundCallback,
  LedgerPayments,
  Queries,
  Calendar,
  Tasks,
} from "@areas";
import { useEffect, forwardRef, useImperativeHandle } from "react";
import { Helmet } from "react-helmet";
import { Route, Routes } from "react-router-dom";
import PortalTemplate from "./portal-template";
import Support from "./system-pages/Support";
import { usePostHog } from "posthog-js/react";
import NotAuthorized from "./system-pages/NotAuthorized";
import NotFound from "./system-pages/NotFound";
import { setPostHogInstance } from "./utils/consoleOverride";
import { CustomerGroupsProvider } from "@context/CustomerGroupsContext";
import { PaymentProfilesProvider } from "@context/PaymentProfilesContext";
import { PaymentRequestTemplatesProvider } from "@context/PaymentRequestTemplatesContext";
import { PaymentRequestDrawerProvider } from "@context/PaymentRequestDrawerContext";
import { TransactionDrawerProvider } from "@context/TransactionDrawerContext";
import { PayoutDrawerProvider } from "@context/PayoutDrawerContext";
import { NoteDrawerProvider } from "@context/NoteDrawerContext";
import { TaskDrawerProvider } from "@context/TaskDrawerContext";
import { useRequestTemplates, useQueries, useAiFeatures } from "@flags";
import { RealtimeProvider } from "./realtime/RealtimeProvider";

function AppContent() {
  const posthog = usePostHog();
  const requestTemplatesEnabled = useRequestTemplates();
  const aiEnabled = useAiFeatures();
  const queriesEnabled = useQueries();
  const { user } = useUser();
  const { merchant } = useMerchant();

  useEffect(() => {
    if (posthog) setPostHogInstance(posthog);
  }, [posthog]);

  useEffect(() => {
    if (!posthog || !user || !merchant) return;

    const { id, email, name, permissions } = user;

    posthog.identify(id, {
      email,
      name,
      merchant_name: merchant.name,
      merchant_id: merchant.id,
      permissions,
    });

    // Attach group context
    posthog.group("merchant", merchant.id, {
      name: merchant.name,
      //modules,
      short_id: merchant.organisationId,
      //branches,
    });

    // Ensure flags are re-evaluated with the identified user + group
    posthog.reloadFeatureFlags();

    // Optional: capture a “logged_in” or similar event here
    // posthog.capture('user_identified');
  }, [posthog, user, merchant]);

  function tokenRequired(component) {
    return <AuthGuard component={() => <TokenGuard component={component} />} />;
  }

  function userRequired(Component) {
    return tokenRequired(() => (
      <UserGuard>
        <MerchantGuard>
          <Component />
        </MerchantGuard>
      </UserGuard>
    ));
  }

  const portalRoutes = [
    {
      path: "/",
      element: <Requests />,
    },
    {
      path: "/requests",
      element: <Requests />,
    },
    {
      path: "/requests/:requestId",
      element: <Requests />,
    },
    {
      path: "/invoicerequests",
      element: <Requests />,
    },
    {
      path: "/settings/*",
      element: <Settings />,
    },
    {
      path: "/transactions",
      element: <Transactions />,
      permission: readPayment,
    },
    !requestTemplatesEnabled && {
      path: "/templates",
      element: <Templates />,
      permission: readMessageTemplate,
    },
    {
      path: "/accounts",
      element: <Accounts />,
      permission: readCustomer,
    },
    {
      path: "/accounts/groups/:groupId",
      element: <Accounts />,
      permission: readCustomer,
    },
    {
      path: "/trade-accounts",
      element: <Accounts />,
      permission: readCustomer,
    },
    {
      path: "/collections",
      element: <Collections />,
      permission: readCustomer,
    },
    {
      path: "/payouts",
      element: <Payouts />,
      permission: readCardPayout,
    },
    {
      path: "/ledgerpayments",
      element: <LedgerPayments />,
      permission: readLedgerPayment,
    },
    {
      path: "/calendar",
      element: <Calendar />,
      requireAi: true,
    },
    {
      path: "/calendar/items/:itemId",
      element: <Calendar />,
      requireAi: true,
    },
    {
      path: "/queries",
      element: <Queries />,
      permission: readCustomer,
      requireQueries: true,
    },
    {
      path: "/tasks",
      element: <Tasks />,
      requireAi: true,
    },
    {
      path: "/support",
      element: <Support />,
    },
  ].filter(Boolean);

  return (
    <Routes>
      <Route path="/logout" element={<LogoutPage />} />
      <Route
        path="/authoriserefundcallback"
        element={<AuthoriseRefundCallback />}
      />
      <Route element={userRequired(PortalTemplate)}>
        {portalRoutes.map((x, i) => {
          const flagged =
            (x.requireAi && !aiEnabled) ||
            (x.requireQueries && !queriesEnabled);

          const element = flagged ? (
            <NotFound />
          ) : !x.permission ? (
            x.element
          ) : (
            <PermissionsGuard permission={x.permission}>
              {x.element}
            </PermissionsGuard>
          );

          return <Route path={x.path} key={i} element={element} />;
        })}
      </Route>
      <Route path="/forbidden" element={<NotAuthorized />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default forwardRef(function App(props, ref) {
  const posthog = usePostHog();

  const onLogin = () => {
    posthog?.capture("clicked_log_in");
  };

  useImperativeHandle(
    ref,
    () => ({
      onLogin,
    }),
    [posthog],
  );

  const nr_script = () => {
    if (
      window.location.hostname.includes("dev") ||
      window.location.host === "localhost:3000"
    ) {
      return "js/nr-dev-min.js";
    } else if (window.location.hostname.includes("staging")) {
      return "js/nr-staging-min.js";
    } else {
      return "js/nr-prod-min.js";
    }
  };

  return (
    <>
      <Helmet>
        <script type="text/javascript" src={nr_script()} />
      </Helmet>
      <RealtimeProvider>
        <UserProvider>
          <MerchantProvider>
            <CustomerGroupsProvider>
              <PaymentProfilesProvider>
                <PaymentRequestTemplatesProvider>
                  <PaymentRequestDrawerProvider>
                    <TransactionDrawerProvider>
                      <PayoutDrawerProvider>
                        <NoteDrawerProvider>
                          <TaskDrawerProvider>
                            <AppContent />
                          </TaskDrawerProvider>
                        </NoteDrawerProvider>
                      </PayoutDrawerProvider>
                    </TransactionDrawerProvider>
                  </PaymentRequestDrawerProvider>
                </PaymentRequestTemplatesProvider>
              </PaymentProfilesProvider>
            </CustomerGroupsProvider>
          </MerchantProvider>
        </UserProvider>
      </RealtimeProvider>
    </>
  );
});
